import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { filter, orderBy, uniq } from 'lodash';
import * as api from 'actions';
import { Loader, NoRecord, Button } from 'components/generic';
import { groups } from 'config';
import { isFailedResponse } from 'helpers/apiResponse';
import ChartCard from './chart-card';
import './style.scss';

// Same entity/product/team/branch derivation collection-dashboard runs per selected report
// (getEntityProductTeams), computed once here since it doesn't vary per report.
function deriveEntityProductTeamBranch() {
    const entityPaths = groups.map(el => el.path?.includes('_CLIENT') && el.path.split('/')[2]);
    const productPaths = groups.map(el => el.path?.includes('_CLIENT') && el.path.split('/')[3]);
    const branchPaths = groups.map(el => ['_HIERARCHY'].includes(el?.path) && el.path.split('/').pop());
    return {
        entity: uniq(entityPaths.filter(Boolean)).toString(),
        product: uniq(productPaths.filter(Boolean)).toString(),
        team: groups.map(el => el.id),
        branch: branchPaths.filter(Boolean).toString(),
    };
}

export default function Dashboard() {
    const { entity, product, team, branch } = useMemo(deriveEntityProductTeamBranch, []);

    const { data, isLoading, refetch } = useQuery(['reportList'], () => api.post('list', {}), {
        refetchOnWindowFocus: false,
    });

    const reportList = useMemo(() => {
        if (isFailedResponse(data)) return [];
        const res = !data?.data?.data?.length ? [] : data.data.data[0];
        const dashboardReports = filter(res?.result, el => (
            el?.isDeleted !== 'Y'
            && el?.dashboardEnabled === 'Y'
            && el.reportType?.includes('SUMMARY_VIEW')
        ));
        return orderBy(dashboardReports, report => Number(report.dashboardOrder), ['asc']);
    }, [data]);

    if (isLoading) return <Loader loading />;

    const listFailed = isFailedResponse(data);

    return (
        <div className="dashboard">

            {listFailed && (
                <div className="dashboard__error">
                    <p>Couldn't load the report list.</p>
                    <Button small primary label="Retry" handleOnSubmit={refetch} />
                </div>
            )}

            {!listFailed && !reportList.length && <NoRecord description="No reports available." />}

            {!listFailed && reportList.length > 0 && (
                <div className="dashboard__grid">
                    {reportList.map(report => (
                        <ChartCard key={report.reportId} report={report} entity={entity} product={product} team={team} branch={branch} />
                    ))}
                </div>
            )}
        </div>
    );
}
